import React, { useState, useCallback, useMemo, useEffect, useRef } from "react"
import type { ImageProps } from "next/image"
import NextImage from "next/image"
import { Flex } from "@chakra-ui/react"
import { isStorybook } from "utils/storybookUtils"

import { baseAttrs, backgroundAttrs, heightAttrs } from "./helpers"
import { colours } from "@shared/ui.design-system"

type Props = {
  src: string
  size?: string
  width?: number
  height?: number
  fullHeight?: boolean
  alt?: string
  opacity?: string
  title?: string
  objectFit?: ImageProps["objectFit"]
  objectPosition?: ImageProps["objectPosition"]
  fillColor?: string
  aspectRatio?: "1:1" | "4:5" | "16:9"
  "data-testid"?: string
}

export const Image: React.FC<Props> = ({
  src,
  size = "standard",
  width,
  height,
  alt,
  fullHeight = true,
  opacity,
  title,
  objectFit = "cover",
  objectPosition,
  fillColor = colours.secondary.light,
  aspectRatio,
  "data-testid": testid,
}) => {
  const [loading, setLoading] = useState(true)
  const [imageWidth, setImageWidth] = useState(0)
  const [imageHeight, setImageHeight] = useState(0)

  const loaded = useCallback(() => setLoading(false), [setLoading])
  const backgroundRef = useRef<HTMLDivElement>(null)
  const attrs = useMemo(
    () => ({
      ...baseAttrs(),
      ...heightAttrs(size),
      ...backgroundAttrs(loading, fillColor),
    }),
    [size, loading, fillColor]
  )

  const isGif = useMemo(() => /\.gif$/.test(src), [src])

  const unoptimizedComputed = useMemo(
    () =>
      process.env.STORYBOOK === "true" || process.env.NODE_ENV === "test"
        ? true
        : isGif,
    [isGif]
  )

  useEffect(() => {
    if (!backgroundRef.current) return

    const { clientWidth, clientHeight } = backgroundRef.current

    const getComputedWith = () => {
      let computedWidth = clientWidth

      if (width && height) {
        computedWidth =
          clientWidth > 100 // Avoid badly computed widths
            ? clientWidth
            : Math.round((2 * (Number(width) * clientHeight)) / Number(height))
      }

      return computedWidth
    }

    const computedWidth = getComputedWith()

    setImageWidth(computedWidth)
    const aspectRatios = {
      "1:1": computedWidth,
      "4:5": (5 * computedWidth) / 4,
      "16:9": (9 * computedWidth) / 16,
    }
    if (aspectRatio) {
      setImageHeight(aspectRatios[aspectRatio])
    } else {
      const computedHeight = Math.round(
        (computedWidth / Number(width || computedWidth)) * Number(height)
      )
      setImageHeight(computedHeight)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [backgroundRef.current?.clientWidth])

  return (
    <Flex height={fullHeight ? "100%" : undefined}>
      <Flex
        width="full"
        css={{
          "& > span": {
            width: "100%",
          },
          opacity,
        }}
        {...attrs}
        data-size={size}
        data-testid="image"
        ref={backgroundRef}
        visibility={imageWidth ? "visible" : "hidden"}
      >
        <NextImage
          src={src}
          width={imageWidth}
          height={imageHeight}
          objectFit={objectFit}
          objectPosition={objectPosition}
          unoptimized={unoptimizedComputed}
          alt={String(alt)}
          title={title}
          onLoad={loaded}
          data-testid={testid}
          priority={isStorybook}
          style={{
            maxWidth: "100%",
            height: "auto",
          }}
        />
      </Flex>
    </Flex>
  )
}
